// src/screens/sales/saleItem/hooks/useSaleItem.ts

import { useCallback, useEffect, useState, useMemo, useRef } from 'react'
import { SaleItemInterface } from '../../../../common/types/interfaces'
import { getSalesApi } from '../../../../salesApi/salesApi'
import { selectCurrentCluster } from '../../../../store/slices/currentClusterSlice'
import { useSelector } from 'react-redux'
import { SEARCH_DEBOUNCE_MS } from '../../../../common/utils/constants'

const initialValue: SaleItemInterface = {
  name: '',
  description: '',
  files: [],
  categoryId: '',
  barCode: '',
  isActive: true,
  useStock: false,
  attributes: [],
  groupId: undefined,
  saleItemId: '',
  unit: '',
  isAddon: false,
  minQuantity: '1',
  quantityStep: '1',
  isService: false,
  kitItemId: '',
  items: [],
}

export const useSaleItem = () => {
  const [modalActive, setModalActive] = useState<boolean>(false)
  const [detailsModalActive, setDetailsModalActive] = useState<boolean>(false)
  const [edit, setEdit] = useState<boolean>(false)
  const [salesApi] = useState(getSalesApi())
  const [data, setData] = useState<SaleItemInterface>(initialValue)
  const [list, setList] = useState<SaleItemInterface[]>([])
  const [current, setCurrent] = useState<SaleItemInterface>()
  const currentCluster = useSelector(selectCurrentCluster)
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const debounceTimerRef = useRef<NodeJS.Timeout | number | null>(null)

  const getList = useCallback(async () => {
    try {
      const result = await salesApi.getSaleItemList({ sort: 'name', order: 'ASC' })
      if (result) setList(result.data)
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }, [salesApi])

  const startCreate = (qr?: string) => {
    setCurrent(undefined)
    setData((prev) => ({ ...prev, barCode: qr }))
    setModalActive(true)
  }

  const startEdit = (el: SaleItemInterface) => {
    setCurrent({ ...el })
    setEdit(true)
    setModalActive(true)
  }

  const startDuplicate = useCallback((sourceItem: SaleItemInterface) => {
    const {
      id,
      createdAt,
      updatedAt,
      totalFavorites,
      totalViews,
      averageRating,
      totalRatings,
      category,
      prices,
      stock_stores,
      ratings,
      selected,
      views,
      iiko_id,
      c1_id,
      moysklad_id,
      ...rest
    } = sourceItem

    const duplicatedItem: SaleItemInterface = {
      ...rest,
      name: `${sourceItem.name} (копия)`,
      barCode: '',
      files: sourceItem.files,
      attributes: [],
      addons: [],
      recommendations: [],
      items: (sourceItem.items || []).map(({ childItemId, quantity }) => ({
        childItemId,
        quantity,
      })),
      groupId: sourceItem.groupId,
      minQuantity: sourceItem.minQuantity,
      quantityStep: sourceItem.quantityStep,
      isActive: sourceItem.isActive,
      useStock: sourceItem.useStock,
      unit: sourceItem.unit,
      categoryId: sourceItem.categoryId,
      isAddon: sourceItem.isAddon,
      description: sourceItem.description,
    }

    setCurrent(duplicatedItem as SaleItemInterface)
    setEdit(false)
    setData(duplicatedItem as SaleItemInterface)
    setModalActive(true)
  }, [])

  const showDetails = useCallback((el: SaleItemInterface) => {
    setCurrent(el)
    setDetailsModalActive(true)
  }, [])

  const handleSearch = (value: string) => {
    setSearchQuery(value)

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(value)
    }, SEARCH_DEBOUNCE_MS)
  }

  const clearSearch = () => {
    setSearchQuery('')
    setDebouncedSearchQuery('')
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
  }

  const filteredList = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return list
    }

    const query = debouncedSearchQuery.toLowerCase().trim()

    return list.filter((item) => {
      return (
        item.id?.toString().toLowerCase().includes(query) ||
        item.name?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
      )
    })
  }, [list, debouncedSearchQuery])

  const qrAction = useCallback(
    (qr: string) => {
      const item = list.find((el) => el.barCode === qr)
      if (item) {
        showDetails(item)
        return
      }
      startCreate(qr)
    },
    [list, showDetails],
  )

  useEffect(() => {
    if (currentCluster) getList()
  }, [currentCluster, getList])

  useEffect(() => {
    if (!modalActive) setData(initialValue)
  }, [modalActive])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return {
    getList,
    modalActive,
    setModalActive,
    startCreate,
    startEdit,
    startDuplicate,
    edit,
    data,
    setData,
    list: filteredList,
    detailsModalActive,
    setDetailsModalActive,
    showDetails,
    current,
    qrAction,
    loading,
    searchQuery,
    handleSearch,
    clearSearch,
  }
}
